import axios from "./axios"
import { handleResponse } from "./util";

interface AdvertiserProfile {
  states_with_license: string[]
}

interface AlertBubble {
  id: number,
  header: string,
  content: string,
  page: string
}

interface KpiAdProfile {
  brand_id: number,
  brand_campaign_id: number,
  end_time: string,
  start_time: string
}

interface KpiProfile extends KpiAdProfile {
  page: number,
  scope: string
}

interface SuggestedTag {
  id: number,
  name: string,
  retailer: string,
  state: string,
  merchant_id: number
}

interface Profile {
  address: string,
  business_phone: string,
  city: string,
  contact_name: string,
  contact_phone: string,
  cost_per_message: number,
  created_at: string,
  email: string,
  id: number,
  key_terms: string,
  latest_ad_start: string,
  meta_data: object,
  name: string,
  postal: string,
  state: string,
  states_with_license: string[],
  time_zone: string
}

interface List {
  brandId: number,
  urlString: string,
  extraParams: string,
  page: number
}

interface KpiParams extends KpiAdProfile {
  merchant_id: number[],
  previous_start: number,
  prevous_end: number
}

export const brandsAPI = {
  fetchSuggestedTags(advertiserID: number): Promise<SuggestedTag[]> {
    return axios.get(`/brands/${advertiserID}/linked_tags/suggested`)
      .then(handleResponse)
  },
  fetchAlerts(advertiserID: number): Promise<AlertBubble[]> {
    return axios.get(`/brands/${advertiserID}/configurations/alert_bubbles`)
      .then((resp) => {
        if (!resp.data) throw new Error('error, please refresh or contact support')
        return resp.data
      })
  },
  getPerformanceKPI(params: Partial<KpiProfile>): Promise<unknown>{
    return axios.get('/ads',
      {
        params: params
      }).then(handleResponse)
  },
  getPerformanceAdKPI(type: string, params: KpiAdProfile): Promise<unknown> {
    return axios.get(`/performance/${type}`,
      {
        params: params
      }).then(handleResponse)
  },
  getAdvertiserCreditUsage(advertiserID: number, page: number = 1): Promise<unknown> {
    return axios.get(`/brands/${advertiserID}/credit_usage`, {
      params: {
        page
      }
    }).then(handleResponse)
  },
  getOrganizationCreditUsage(advertiserID: number, page: number = 1): Promise<unknown>{
    return axios.get(`/brand_organizations/credit_usage`, {
      params: {
        brand_id: advertiserID,
        page
      }
    }).then(handleResponse)
  },
  updateAdvertiser(advertiserID: number, data: Partial<AdvertiserProfile>): Promise<Profile> {
    return axios.patch(`/brands/${advertiserID}`, data)
      .then(handleResponse)
  },
  checkBillingUser(brandId: number, id: number): Promise<unknown> {
    return axios.get(`/users/${id}?brand_id=${brandId}`)
      .then(handleResponse)
  },
  getUserAdvertisers() {
    return axios.get('/brands?per_page=200').then(handleResponse)
  },

  /**
   * API request to get inventory & sponsor requests of current advertiser
   * @param {number} advertiserId - current advertiser ID
   * @param {number} page - current page
   * @returns {object} { requests: [...], pagination: {...} }
   */
  getMerchantRequests(advertiserId: number, page: number) {
    return axios.get(`/marketplace_requests`, {
      params: {
        brand_id: advertiserId,
        page: page || 1
      }
    }).then(handleResponse)
  },

  /**
   * API request to update status of request to dismissed
   * @param {number} advertiserId - current advertiser ID
   * @param {number} requestId - dismissed request ID
   * @returns {object} dismissed request info
   */
  dismissMerchantRequest(advertiserId: number, requestId: number) {
    return axios.patch(`/marketplace_requests/${requestId}/dismiss`, {
      brand_id: advertiserId
    }).then(handleResponse)
  },

  /**
   * GET request to retrieve retailers/sponsor requests list on step 1 ad creation
   * @param {List} params - contain current page, search term, current advertiser ID, url
   * @returns {object} has array of retailers/sponsor requests and pagination data
   */
  getList(params: List) {
    const requestUrl = params.urlString + `${params.extraParams || ''}` + `&page=${params.page}`
    return axios.get(requestUrl)
    .then(handleResponse)
  },
  /**
   * GET request to retrieve a certain kpi with passed url
   * @param {string} kpiUrl - url for certain kpi
   * @param {KpiParams} params - will have partial attributes within KpiParams interface,
   * depending on kpi. This will be used for kpis on performance and dashboard page and they
   * will have different params passed in.
   */
  getKpi(kpiUrl: string, params: Partial<KpiParams>) {
    return axios.get(kpiUrl, { params }).then(handleResponse)
  }
};
